import toArray from "lodash/toArray";

import { useTranslator } from "@eo-locale/react";

import { CommonEmojiQuestion } from "@/storybook-components";
import { questionNumber, questionTitleWithNumber } from "@/helpers/survey";
import { setSurveyAnswers } from "@/redux/features/survey/slice";
import type { Question } from "@/types";

import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import { spacing, textStyles } from "./question-styles";
import { surveyParticipantFieldControl } from "./survey-participant-test-ids";

interface IEmojiQuestion {
  question: Question;
  participantFieldId: string;
}

const EmojiQuestion = ({
  question,
  participantFieldId
}: IEmojiQuestion): JSX.Element => {
  const dispatch = useAppDispatch();
  const survey = useAppSelector((state) => state.survey);
  const { answers, errors, options, saving } = survey;

  const error = errors[question.id] || {};
  const answer = answers[question.id]?.id ?? null;

  // ToDo: Remove after BE implementation
  const sortedOptions = toArray(options)
    .filter((option) => question.id === option.questionId)
    .sort((a, b) => (a.sequence > b.sequence ? 1 : -1));

  const handleChange = (value: any): void => {
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: { id: parseInt(value) }
      })
    );
  };

  const { translate } = useTranslator();
  return (
    <CommonEmojiQuestion
      layoutProps={{ width: "100%", as: "fieldset" }}
      invalid={!!error.code}
      required={question.required}
      disabled={saving}
      className={error.code ? "ehq-question-error" : null}
      gap={spacing.question}
      questionId={question.id}
      questionLabel={questionTitleWithNumber(
        questionNumber(question, survey),
        question
      )}
      questionLabelProps={{ ...textStyles?.label }}
      questionLabelType="legend"
      labelClassName="ehq-surveyQuestionHeading"
      accessibilityTextOfRequired={`* ${translate("common.required")}`}
      answerStackSpacing={spacing.answerStack}
      contentLayoutProps={{ py: 4 }}
      options={sortedOptions}
      reverseOptions={!!question.reverseOptions}
      handleChange={handleChange}
      answer={answer}
      answerProps={{ ...textStyles?.content }}
      footerStackSpacing={spacing.footerStack}
      note={question.notes}
      noteProps={{ ...textStyles?.footer }}
      errorMessage={error?.code && translate(`errors.${error.code}`)}
      errorMessageProps={{ ...textStyles?.error }}
      getEmojiOptionDataTestId={(optionId) =>
        surveyParticipantFieldControl(
          participantFieldId,
          `emoji-option-${sortedOptions.findIndex(
            (o) => String(o.id) === String(optionId)
          )}`
        )
      }
    />
  );
};

export default EmojiQuestion;
